// Enhanced sessionStorageService.ts with security improvements

import { securityEventLogger } from './securityValidationUtils'

// 🔧 OPTIMIZED: Conditional debugging flag
const DEBUG_SESSION_STORAGE = process.env.NODE_ENV === 'development' && false // Set to true only when debugging

// Enhanced interface with security metadata
export interface SessionStorageModel {
  sessionId?: string
  loginTime?: number
  publicKey?: string
  secretKey?: string
  csrfToken?: string
  clientPrivateKey?: CryptoKey // typically not serializable so keep in memory only
  // New security fields
  sessionFingerprint?: string
  lastValidated?: number
  securityScore?: number
}

/**
 * Enhanced AuthSessionStorageService with security improvements
 * Manages session data with integrity validation and security monitoring
 */
export class EnhancedAuthSessionStorageService {
  private _sessionStorageModel: SessionStorageModel = {}
  private readonly MAX_STORAGE_ATTEMPTS = 3
  private readonly STORAGE_RETRY_DELAY = 100

  constructor() {
    this.rehydrateFromStorage()
  }

  /**
   * Rehydrates session data from localStorage with validation
   * @private
   */
  private rehydrateFromStorage(): void {
    try {
      // Rehydrate persistent values from localStorage with validation
      const sessionId = this.safeGetFromStorage('sessionId')
      if (sessionId && this.isValidSessionId(sessionId)) {
        this._sessionStorageModel.sessionId = sessionId
      }

      const loginTimeStr = this.safeGetFromStorage('loginTime')
      if (loginTimeStr) {
        const loginTime = parseInt(loginTimeStr, 10)
        if (!isNaN(loginTime) && loginTime > 0 && loginTime <= Date.now()) {
          this._sessionStorageModel.loginTime = loginTime
        } else {
          securityEventLogger.logSessionAnomaly('Invalid login time during rehydration', {
            loginTimeStr
          })
          this.safeRemoveFromStorage('loginTime')
        }
      }

      const publicKey = this.safeGetFromStorage('publicKey')
      if (publicKey && this.isValidKeyFormat(publicKey, 'public')) {
        this._sessionStorageModel.publicKey = publicKey
      }

      const secretKey = this.safeGetFromStorage('secretKey')
      if (secretKey && this.isValidKeyFormat(secretKey, 'secret')) {
        this._sessionStorageModel.secretKey = secretKey
      }

      const csrfToken = this.safeGetFromStorage('csrfToken')
      if (csrfToken && this.isValidToken(csrfToken)) {
        this._sessionStorageModel.csrfToken = csrfToken
      }

      const sessionFingerprint = this.safeGetFromStorage('sessionFingerprint')
      if (sessionFingerprint) {
        this._sessionStorageModel.sessionFingerprint = sessionFingerprint
      }

      const lastValidatedStr = this.safeGetFromStorage('lastValidated')
      if (lastValidatedStr) {
        const lastValidated = parseInt(lastValidatedStr, 10)
        if (!isNaN(lastValidated)) {
          this._sessionStorageModel.lastValidated = lastValidated
        }
      }

      const securityScoreStr = this.safeGetFromStorage('securityScore')
      if (securityScoreStr) {
        const securityScore = parseInt(securityScoreStr, 10)
        if (!isNaN(securityScore) && securityScore >= 0 && securityScore <= 100) {
          this._sessionStorageModel.securityScore = securityScore
        }
      }

    } catch (error) {
      securityEventLogger.logSessionAnomaly('Error during session rehydration', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      // Clear potentially corrupted data
      this.clearSession()
    }
  }

  /**
   * Safely retrieves a value from localStorage with error handling
   * @private
   */
  private safeGetFromStorage(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch (error) {
      securityEventLogger.logSessionAnomaly(`Failed to read ${key} from localStorage`, {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return null
    }
  }

  /**
   * Safely stores a value in localStorage with retry logic
   * @private
   */
  private async safeSetToStorage(key: string, value: string): Promise<boolean> {
    for (let attempt = 1; attempt <= this.MAX_STORAGE_ATTEMPTS; attempt++) {
      try {
        localStorage.setItem(key, value)
        return true
      } catch (error) {
        securityEventLogger.logSessionAnomaly(`Failed to store ${key} (attempt ${attempt})`, {
          error: error instanceof Error ? error.message : 'Unknown error',
          attempt
        })
        
        if (attempt < this.MAX_STORAGE_ATTEMPTS) {
          await this.delay(this.STORAGE_RETRY_DELAY * attempt)
        }
      }
    }
    return false
  }

  /**
   * Safely removes a value from localStorage
   * @private
   */
  private safeRemoveFromStorage(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      securityEventLogger.logSessionAnomaly(`Failed to remove ${key} from localStorage`, {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  /**
   * Validates session ID format
   * @private
   */
  private isValidSessionId(sessionId: string): boolean {
    return typeof sessionId === 'string' && 
           sessionId.length >= 10 && 
           sessionId.length <= 200 &&
           !/[<>\"'`]/.test(sessionId)
  }

  /**
   * Validates key format based on type
   * @private
   */
  private isValidKeyFormat(key: string, type: 'public' | 'secret'): boolean {
    if (typeof key !== 'string' || key.length < 10) return false
    
    if (type === 'public') {
      return key.includes('-----BEGIN PUBLIC KEY-----') && 
             key.includes('-----END PUBLIC KEY-----')
    }
    
    // For secret keys, just check it's not empty and reasonable length
    return key.length >= 10 && key.length <= 1000
  }

  /**
   * Validates token format
   * @private
   */
  private isValidToken(token: string): boolean {
    return typeof token === 'string' && 
           token.length >= 10 && 
           token.length <= 500 &&
           !/[<>\"'`]/.test(token)
  }

  /**
   * Simple delay utility for retry logic
   * @private
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Enhanced public key setter with validation
   */
  async setPublicKey(publicKey: string): Promise<boolean> {
    if (!this.isValidKeyFormat(publicKey, 'public')) {
      securityEventLogger.logSessionAnomaly('Attempted to set invalid public key format')
      return false
    }

    this._sessionStorageModel.publicKey = publicKey
    const stored = await this.safeSetToStorage('publicKey', publicKey)
    
    if (stored) {
      this.updateLastValidated()
      securityEventLogger.logSecurityValidation('public_key_stored')
    }
    
    return stored
  }

  /**
   * Retrieves the public key with validation
   */
  getPublicKey(): string | undefined {
    const publicKey = this._sessionStorageModel.publicKey
    
    if (publicKey && !this.isValidKeyFormat(publicKey, 'public')) {
      securityEventLogger.logSessionAnomaly('Retrieved invalid public key, clearing')
      this.clearPublicKey()
      return undefined
    }
    
    return publicKey
  }

  /**
   * Enhanced client private key setter with validation
   */
  setClientPrivateKey(clientPrivateKey: CryptoKey | undefined): void {
    this._sessionStorageModel.clientPrivateKey = clientPrivateKey
    
    if (clientPrivateKey) {
      this.updateLastValidated()
      securityEventLogger.logSecurityValidation('client_private_key_stored')
    }
    // Note: crypto keys typically cannot be stored in localStorage.
  }

  /**
   * Retrieves the client private key
   */
  getClientPrivateKey(): CryptoKey | undefined {
    return this._sessionStorageModel.clientPrivateKey
  }

  /**
   * Enhanced secret key setter with validation
   */
  async setSecretKey(secretKey: string): Promise<boolean> {
    if (!this.isValidKeyFormat(secretKey, 'secret')) {
      securityEventLogger.logSessionAnomaly('Attempted to set invalid secret key format')
      return false
    }

    this._sessionStorageModel.secretKey = secretKey
    const stored = await this.safeSetToStorage('secretKey', secretKey)
    
    if (stored) {
      this.updateLastValidated()
      securityEventLogger.logSecurityValidation('secret_key_stored')
    }
    
    return stored
  }

  /**
   * Retrieves the secret key with validation
   */
  getSecretKey(): string | undefined {
    const secretKey = this._sessionStorageModel.secretKey
    
    if (secretKey && !this.isValidKeyFormat(secretKey, 'secret')) {
      securityEventLogger.logSessionAnomaly('Retrieved invalid secret key, clearing')
      this.clearSecretKey()
      return undefined
    }
    
    return secretKey
  }

  /**
   * Enhanced login time setter with validation
   */
  async setLoginTime(): Promise<boolean> {
    const now = Date.now()
    this._sessionStorageModel.loginTime = now
    
    const stored = await this.safeSetToStorage('loginTime', now.toString())
    
    if (stored) {
      this.updateLastValidated()
      this.updateSecurityScore(85) // Good security score for fresh login
      securityEventLogger.logSecurityValidation('login_time_set', { loginTime: now })
    }
    
    return stored
  }

  /**
   * Retrieves the login time with validation
   */
  getLoginTime(): number | undefined {
    const loginTime = this._sessionStorageModel.loginTime
    
    if (loginTime !== undefined) {
      const now = Date.now()
      
      // Validate login time is not in the future and not too old
      if (loginTime > now) {
        securityEventLogger.logSessionAnomaly('Login time is in the future, clearing')
        this.clearLoginTime()
        return undefined
      }
      
      const maxAge = 24 * 60 * 60 * 1000 // 24 hours
      if (now - loginTime > maxAge) {
        securityEventLogger.logSessionAnomaly('Login time is too old, clearing')
        this.clearLoginTime()
        return undefined
      }
    }
    
    return loginTime
  }

  /**
   * Enhanced session ID setter with validation
   */
  async setSession(sessionId?: string): Promise<boolean> {
    if (sessionId && !this.isValidSessionId(sessionId)) {
      securityEventLogger.logSessionAnomaly('Attempted to set invalid session ID format')
      return false
    }

    this._sessionStorageModel.sessionId = sessionId
    
    let stored = true
    if (sessionId) {
      stored = await this.safeSetToStorage('sessionId', sessionId)
      if (stored) {
        this.updateLastValidated()
        this.updateSecurityScore(80) // Good security score for active session
        securityEventLogger.logSecurityValidation('session_id_set')
      }
    } else {
      this.safeRemoveFromStorage('sessionId')
    }
    
    return stored
  }

  /**
   * Enhanced session ID retrieval with validation and rehydration
   */
  getSession(): string | undefined {
    // 🔧 OPTIMIZED: Conditional logging only when debugging
    if (DEBUG_SESSION_STORAGE) {
      console.log('session id', this._sessionStorageModel.sessionId)
    }
    
    if (!this._sessionStorageModel.sessionId) {
      if (DEBUG_SESSION_STORAGE) {
        console.log('no session found, getting from ls')
      }
      
      const storedSessionId = this.safeGetFromStorage('sessionId')
      
      if (DEBUG_SESSION_STORAGE) {
        console.log('local storage sessionId', storedSessionId)
      }
      
      if (storedSessionId && this.isValidSessionId(storedSessionId)) {
        this._sessionStorageModel.sessionId = storedSessionId
      } else if (storedSessionId) {
        securityEventLogger.logSessionAnomaly('Invalid session ID in localStorage, clearing')
        this.safeRemoveFromStorage('sessionId')
      }
        
      if (DEBUG_SESSION_STORAGE) {
        console.log('session id set from ls', this._sessionStorageModel.sessionId)
      }
    }
    
    if (DEBUG_SESSION_STORAGE) {
      console.log('returning session id', this._sessionStorageModel.sessionId)
      console.log('all session data', this._sessionStorageModel)
    }
    
    return this._sessionStorageModel.sessionId
  }

  /**
   * Enhanced CSRF token retrieval with validation
   */
  getCsrfToken(): string | undefined {
    if (!this._sessionStorageModel.csrfToken) {
      const storedToken = this.safeGetFromStorage('csrfToken')
      
      if (storedToken && this.isValidToken(storedToken)) {
        this._sessionStorageModel.csrfToken = storedToken
      } else if (storedToken) {
        securityEventLogger.logSessionAnomaly('Invalid CSRF token in localStorage, clearing')
        this.safeRemoveFromStorage('csrfToken')
      }
    }
    
    return this._sessionStorageModel.csrfToken
  }

  /**
   * Enhanced CSRF token setter with validation
   */
  async setCsrfToken(csrfToken?: string): Promise<boolean> {
    if (csrfToken && !this.isValidToken(csrfToken)) {
      securityEventLogger.logSessionAnomaly('Attempted to set invalid CSRF token format')
      return false
    }

    this._sessionStorageModel.csrfToken = csrfToken
    
    let stored = true
    if (csrfToken) {
      stored = await this.safeSetToStorage('csrfToken', csrfToken)
      if (stored) {
        this.updateLastValidated()
        securityEventLogger.logSecurityValidation('csrf_token_set')
      }
    } else {
      this.safeRemoveFromStorage('csrfToken')
    }
    
    return stored
  }

  /**
   * Enhanced session fingerprint management
   */
  async setSessionFingerprint(fingerprint: string): Promise<boolean> {
    if (!fingerprint || fingerprint.length < 10 || fingerprint.length > 100) {
      securityEventLogger.logSessionAnomaly('Invalid session fingerprint format')
      return false
    }

    this._sessionStorageModel.sessionFingerprint = fingerprint
    const stored = await this.safeSetToStorage('sessionFingerprint', fingerprint)
    
    if (stored) {
      this.updateLastValidated()
      securityEventLogger.logSecurityValidation('session_fingerprint_set')
    }
    
    return stored
  }

  /**
   * Retrieves session fingerprint
   */
  getSessionFingerprint(): string | undefined {
    return this._sessionStorageModel.sessionFingerprint
  }

  /**
   * Updates the last validated timestamp
   * @private
   */
  private async updateLastValidated(): Promise<void> {
    const now = Date.now()
    this._sessionStorageModel.lastValidated = now
    await this.safeSetToStorage('lastValidated', now.toString())
  }

  /**
   * Updates the security score
   * @private
   */
  private async updateSecurityScore(score: number): Promise<void> {
    if (score < 0 || score > 100) return
    
    this._sessionStorageModel.securityScore = score
    await this.safeSetToStorage('securityScore', score.toString())
  }

  /**
   * Gets the current security score
   */
  getSecurityScore(): number | undefined {
    return this._sessionStorageModel.securityScore
  }

  /**
   * Gets the last validated timestamp
   */
  getLastValidated(): number | undefined {
    return this._sessionStorageModel.lastValidated
  }

  /**
   * Enhanced session clearing with comprehensive cleanup and logging
   */
  clearSession(): void {
    const hadSession = Boolean(this._sessionStorageModel.sessionId)
    const sessionAge = this._sessionStorageModel.loginTime ? 
      Date.now() - this._sessionStorageModel.loginTime : undefined

    // Log session clearing
    securityEventLogger.logSecurityEvent('session_cleared', {
      hadSession,
      sessionAge,
      securityScore: this._sessionStorageModel.securityScore
    }, 'info')

    // Clear in-memory data
    this._sessionStorageModel = {
      clientPrivateKey: undefined,
      csrfToken: undefined,
      loginTime: undefined,
      publicKey: undefined,
      secretKey: undefined,
      sessionId: undefined,
      sessionFingerprint: undefined,
      lastValidated: undefined,
      securityScore: undefined,
    }

    // Clear storage with error handling
    try {
      sessionStorage.clear()
    } catch (error) {
      securityEventLogger.logSessionAnomaly('Failed to clear sessionStorage', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }

    try {
      localStorage.clear()
    } catch (error) {
      securityEventLogger.logSessionAnomaly('Failed to clear localStorage', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
    
    // 🔧 OPTIMIZED: Conditional logging only when debugging
    if (DEBUG_SESSION_STORAGE) {
      console.log('after clearing session', this._sessionStorageModel)
    }
  }

  /**
   * Clears individual session components
   */
  clearPublicKey(): void {
    this._sessionStorageModel.publicKey = undefined
    this.safeRemoveFromStorage('publicKey')
  }

  clearSecretKey(): void {
    this._sessionStorageModel.secretKey = undefined
    this.safeRemoveFromStorage('secretKey')
  }

  clearLoginTime(): void {
    this._sessionStorageModel.loginTime = undefined
    this.safeRemoveFromStorage('loginTime')
  }

  /**
   * Enhanced session data retrieval with validation
   */
  getAllSessionData(): SessionStorageModel {
    // Validate session data before returning
    const data = { ...this._sessionStorageModel }
    
    // Remove any invalid data
    if (data.sessionId && !this.isValidSessionId(data.sessionId)) {
      data.sessionId = undefined
    }
    
    if (data.publicKey && !this.isValidKeyFormat(data.publicKey, 'public')) {
      data.publicKey = undefined
    }
    
    if (data.secretKey && !this.isValidKeyFormat(data.secretKey, 'secret')) {
      data.secretKey = undefined
    }
    
    if (data.csrfToken && !this.isValidToken(data.csrfToken)) {
      data.csrfToken = undefined
    }
    
    return data
  }

  /**
   * Validates the integrity of all session data
   */
  validateSessionIntegrity(): {
    isValid: boolean
    issues: string[]
    securityScore: number
  } {
    const issues: string[] = []
    let securityScore = 100
    
    const data = this._sessionStorageModel
    
    // Check for required fields
    if (!data.sessionId) {
      issues.push('Missing session ID')
      securityScore -= 25
    } else if (!this.isValidSessionId(data.sessionId)) {
      issues.push('Invalid session ID format')
      securityScore -= 30
    }
    
    if (!data.loginTime) {
      issues.push('Missing login time')
      securityScore -= 20
    } else {
      const now = Date.now()
      if (data.loginTime > now) {
        issues.push('Login time is in the future')
        securityScore -= 25
      } else {
        const age = now - data.loginTime
        const maxAge = 24 * 60 * 60 * 1000
        if (age > maxAge) {
          issues.push('Session expired')
          securityScore -= 30
        }
      }
    }
    
    if (!data.publicKey) {
      issues.push('Missing public key')
      securityScore -= 15
    } else if (!this.isValidKeyFormat(data.publicKey, 'public')) {
      issues.push('Invalid public key format')
      securityScore -= 20
    }
    
    if (!data.secretKey) {
      issues.push('Missing secret key')
      securityScore -= 15
    } else if (!this.isValidKeyFormat(data.secretKey, 'secret')) {
      issues.push('Invalid secret key format')
      securityScore -= 20
    }
    
    securityScore = Math.max(0, Math.min(100, securityScore))
    
    // Update stored security score
    if (securityScore !== this._sessionStorageModel.securityScore) {
      this.updateSecurityScore(securityScore)
    }
    
    return {
      isValid: issues.length === 0 && securityScore >= 70,
      issues,
      securityScore
    }
  }

  /**
   * Performs maintenance on session storage (cleanup, validation)
   */
  performMaintenance(): {
    cleaned: number
    validated: boolean
    securityScore: number
  } {
    let cleaned = 0
    
    // Clean up invalid stored data
    const validationResult = this.validateSessionIntegrity()
    
    if (!validationResult.isValid) {
      // Clean up specific invalid components
      if (this._sessionStorageModel.sessionId && !this.isValidSessionId(this._sessionStorageModel.sessionId)) {
        this.clearPublicKey()
        cleaned++
      }
      
      if (this._sessionStorageModel.publicKey && !this.isValidKeyFormat(this._sessionStorageModel.publicKey, 'public')) {
        this.clearPublicKey()
        cleaned++
      }
      
      if (this._sessionStorageModel.secretKey && !this.isValidKeyFormat(this._sessionStorageModel.secretKey, 'secret')) {
        this.clearSecretKey()
        cleaned++
      }
      
      if (this._sessionStorageModel.loginTime) {
        const now = Date.now()
        const maxAge = 24 * 60 * 60 * 1000
        if (this._sessionStorageModel.loginTime > now || (now - this._sessionStorageModel.loginTime) > maxAge) {
          this.clearLoginTime()
          cleaned++
        }
      }
    }
    
    return {
      cleaned,
      validated: validationResult.isValid,
      securityScore: validationResult.securityScore
    }
  }
}

export const authSessionStorageService = new EnhancedAuthSessionStorageService()

/**
 * Interface for the response when creating a new session.
 */
export interface CreateNewSessionResponseModel {
  keyOne: string
  keyTwo: string
  ttl?: number
}

/**
 * Interface for the response when retrieving a session.
 */
export interface GetSessionResponseModel {
  userName: string
  roles: string[]
  ttl: number
  key: string
}